import { Column } from 'new/elements/Table/types';
import { User } from 'new/entities/User';
import { TableProfile } from 'new/elements/TableProfile';
import React from 'react';
import { ChipBadge } from 'new/elements/ChipBadge';
import { Stack } from '@mui/material';
import { format, isPast } from 'date-fns';
import { Team } from 'new/entities/Team';
import { startCase } from 'lodash';
import {
  getUserRoleLabel,
  USER_ROLE_ADMIN,
  USER_ROLE_COLLABORATOR,
  USER_ROLE_MEMBER,
  USER_ROLE_OPERATOR,
} from 'js/constants/userRoles';
import { EmailCell } from './EmailCell';
import styles from './Members.module.scss';

const roleFilterOptions = [
  { label: getUserRoleLabel(USER_ROLE_ADMIN), value: USER_ROLE_ADMIN },
  { label: getUserRoleLabel(USER_ROLE_OPERATOR), value: USER_ROLE_OPERATOR },
  {
    label: getUserRoleLabel(USER_ROLE_COLLABORATOR),
    value: USER_ROLE_COLLABORATOR,
  },
  { label: getUserRoleLabel(USER_ROLE_MEMBER), value: USER_ROLE_MEMBER },
];

export const columns = [
  {
    name: 'Name',
    field: 'full_name',
    renderRow(row) {
      return <TableProfile name={row.full_name} />;
    },
  },
  {
    name: 'Role',
    field: 'access_role',
    filterable: true,
    filterOptions: roleFilterOptions,
    renderRow(row) {
      const expiresAt = row.access_role_expires_at ? new Date(row.access_role_expires_at) : null;
      const showExpiry = expiresAt && !isPast(expiresAt);
      const tzAbbr = expiresAt
        ? (new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
            .formatToParts(expiresAt)
            .find((p) => p.type === 'timeZoneName')?.value ?? '')
        : '';
      return (
        <Stack direction="column" gap={0.25}>
          <span>{getUserRoleLabel(row.access_role)}</span>
          {showExpiry && (
            <ChipBadge
              label={`Expires ${format(expiresAt, 'MMM d, yyyy h:mm a')}${tzAbbr ? ` ${tzAbbr}` : ''}`}
              color="warning"
              className={styles.expirationChip}
            />
          )}
        </Stack>
      );
    },
  },
  {
    name: 'Email address',
    field: 'email',
    width: 25,
    renderRow(row) {
      return <EmailCell email={row.email} />;
    },
  },
  {
    name: 'Status',
    sortable: false,
    field: 'org_membership_status',
    filterable: true,
    filterOptions: [
      { label: 'Active', value: 'ACTIVE', default: true },
      { label: 'Deactivated', value: 'DEACTIVATED' },
    ],
    renderRow(row) {
      const effectiveStatus = row.org_membership_status || 'ACTIVE';
      const isActive = effectiveStatus === 'ACTIVE';
      return (
        <ChipBadge
          label={startCase(effectiveStatus)}
          dot
          color={isActive ? 'success' : 'disabled'}
        />
      );
    },
  },
  {
    name: 'Teams',
    field: 'teams',
    renderRow({ teams }) {
      return (
        <Stack flexDirection="row" gap={'4px'} flexWrap="wrap">
          {teams.map((team) => (
            <ChipBadge key={team.id} label={team.name} />
          ))}
        </Stack>
      );
    },
  },
] as Column<User & { teams: Team[] }>[];
